/**
 * OS Buddy teaching view (#1608): workshop rules the customer agent follows.
 */

import { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { describeApiError } from '@/lib/describeApiError';
import {
  Box,
  Button,
  Chip,
  CircularProgress,
  DialogContent,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FormDialog } from '@/components/common/FormDialog';
import { ConfirmDialog } from '@/components/common/ConfirmDialog';
import { useNotify } from '@/hooks/useNotify';
import { ApiError, api } from '@/lib/api';
import { queryKeys } from '@/hooks/queryKeys';

type RuleKind = 'constraint' | 'workflow_rule' | 'preference' | 'correction';
type RuleOrigin = 'workshop' | 'os_buddy_suggested' | 'oceanspace';

interface WorkshopRule {
  id: string;
  kind: RuleKind;
  title: string;
  statement: string;
  origin: RuleOrigin;
}

interface RuleSuggestion {
  id: string;
  occurrenceCount: number;
  kind: RuleKind;
  title: string;
  agentText: string;
  staffText: string;
  situation: string;
}

const KINDS: RuleKind[] = ['constraint', 'workflow_rule', 'preference', 'correction'];

function isGovernanceOverride(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 409) return false;
  if (!error.data || typeof error.data !== 'object') return false;
  return (error.data as { code?: unknown }).code === 'GOVERNANCE_OVERRIDE';
}

/** MindPaya, the rules' primary store, refused the write or did not answer (#3940). */
function isWorkshopMemoryUnavailable(error: unknown): boolean {
  if (!(error instanceof ApiError) || error.status !== 502) return false;
  if (!error.data || typeof error.data !== 'object') return false;
  return (error.data as { code?: unknown }).code === 'WORKSHOP_MEMORY_UNAVAILABLE';
}

export function OsBuddyTeachingTab() {
  const { t } = useTranslation('settings');
  const { t: tc } = useTranslation('common');
  const notify = useNotify();
  const queryClient = useQueryClient();

  const listQuery = useQuery({
    queryKey: queryKeys.workshopRules.list(),
    queryFn: () => api.get<{ rules: WorkshopRule[] }>('/assistant/workshop-rules'),
  });
  const suggestionsQuery = useQuery({
    queryKey: queryKeys.correctionSignals.list(),
    queryFn: () => api.get<{ suggestions: RuleSuggestion[] }>('/assistant/correction-signals'),
  });

  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState<WorkshopRule | null>(null);
  const [kind, setKind] = useState<RuleKind>('constraint');
  const [title, setTitle] = useState('');
  const [statement, setStatement] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<WorkshopRule | null>(null);
  const [accepting, setAccepting] = useState<RuleSuggestion | null>(null);
  const [rejecting, setRejecting] = useState<RuleSuggestion | null>(null);

  const isDirty = editing
    ? kind !== editing.kind || title !== editing.title || statement !== editing.statement
    : title.trim().length > 0 || statement.trim().length > 0 || kind !== 'constraint';

  const grouped = useMemo(() => {
    const rules = listQuery.data?.rules ?? [];
    return KINDS.map((k) => ({ kind: k, rules: rules.filter((r) => r.kind === k) }));
  }, [listQuery.data]);

  const save = useMutation({
    mutationFn: async () => {
      const body = { kind, title: title.trim(), statement: statement.trim() };
      if (editing) {
        return api.patch<WorkshopRule>(`/assistant/workshop-rules/${editing.id}`, body);
      }
      return api.post<WorkshopRule>('/assistant/workshop-rules', body);
    },
    onSuccess: async () => {
      notify.success(t('osBuddy.saved'), t('osBuddy.savedDesc'));
      setDialogOpen(false);
      setEditing(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.workshopRules.all });
    },
    onError: (error) => {
      if (isGovernanceOverride(error)) {
        notify.error(t('osBuddy.governanceOverride'), t('osBuddy.governanceOverrideDesc'));
        return;
      }
      if (isWorkshopMemoryUnavailable(error)) {
        notify.error(t('osBuddy.saveFailed'), t('osBuddy.memoryUnavailableDesc'));
        return;
      }
      notify.error(t('osBuddy.saveFailed'), describeApiError(error, tc, { fallback: t('osBuddy.saveFailedDesc') }));
    },
  });

  const remove = useMutation({
    mutationFn: (id: string) => api.delete(`/assistant/workshop-rules/${id}`),
    onSuccess: async () => {
      notify.success(t('osBuddy.deleted'), t('osBuddy.deletedDesc'));
      setDeleteTarget(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.workshopRules.all });
    },
    onError: (error) => {
      notify.error(
        t('osBuddy.deleteFailed'),
        isWorkshopMemoryUnavailable(error) ? t('osBuddy.memoryUnavailableDesc') : t('osBuddy.deleteFailedDesc')
      );
    },
  });

  const acceptSuggestion = useMutation({
    mutationFn: (body: { id: string; kind: RuleKind; title: string; statement: string }) =>
      api.post(`/assistant/correction-signals/${body.id}/accept`, {
        kind: body.kind,
        title: body.title,
        statement: body.statement,
      }),
    onSuccess: async () => {
      notify.success(t('osBuddy.suggestion.accepted'), t('osBuddy.suggestion.acceptedDesc'));
      setAccepting(null);
      await Promise.all([
        queryClient.invalidateQueries({ queryKey: queryKeys.workshopRules.all }),
        queryClient.invalidateQueries({ queryKey: queryKeys.correctionSignals.all }),
      ]);
    },
    onError: (error) => {
      if (isGovernanceOverride(error)) {
        notify.error(t('osBuddy.governanceOverride'), t('osBuddy.governanceOverrideDesc'));
        return;
      }
      notify.error(
        t('osBuddy.suggestion.failed'),
        isWorkshopMemoryUnavailable(error) ? t('osBuddy.memoryUnavailableDesc') : t('osBuddy.suggestion.failedDesc')
      );
    },
  });

  const rejectSuggestion = useMutation({
    mutationFn: (id: string) => api.post(`/assistant/correction-signals/${id}/dismiss`, {}),
    onSuccess: async () => {
      notify.success(t('osBuddy.suggestion.rejected'), t('osBuddy.suggestion.rejectedDesc'));
      setRejecting(null);
      await queryClient.invalidateQueries({ queryKey: queryKeys.correctionSignals.all });
    },
    onError: () => {
      notify.error(t('osBuddy.suggestion.failed'), t('osBuddy.suggestion.failedDesc'));
    },
  });

  const suggestionStatement = (item: RuleSuggestion): string =>
    t('osBuddy.suggestion.statement', { agent: item.agentText, staff: item.staffText });

  const openCreate = () => {
    setEditing(null);
    setKind('constraint');
    setTitle('');
    setStatement('');
    setDialogOpen(true);
  };

  const openEdit = (rule: WorkshopRule) => {
    if (rule.origin === 'oceanspace') return;
    setEditing(rule);
    setKind(rule.kind);
    setTitle(rule.title);
    setStatement(rule.statement);
    setDialogOpen(true);
  };

  const openSuggestionEdit = (item: RuleSuggestion) => {
    setKind(item.kind);
    setTitle(item.title);
    setStatement(suggestionStatement(item));
    setAccepting(item);
  };

  if (listQuery.isLoading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
        <CircularProgress />
      </Box>
    );
  }

  if (listQuery.isError) {
    return (
      <Typography color="error">{t('osBuddy.loadFailed')}</Typography>
    );
  }

  return (
    <Stack spacing={3}>
      <Stack direction="row" justifyContent="space-between" alignItems="flex-start" gap={2}>
        <Box>
          <Typography variant="h6">{t('osBuddy.title')}</Typography>
          <Typography variant="body2" color="text.secondary">
            {t('osBuddy.description')}
          </Typography>
        </Box>
        <Button variant="contained" startIcon={<AddIcon />} onClick={openCreate}>
          {t('osBuddy.add')}
        </Button>
      </Stack>

      {(suggestionsQuery.data?.suggestions ?? []).length > 0 && (
        <Box>
          <Typography variant="h6">{t('osBuddy.suggestion.title')}</Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            {t('osBuddy.suggestion.description')}
          </Typography>
          <Stack spacing={1}>
            {(suggestionsQuery.data?.suggestions ?? []).map((item) => (
              <Stack
                key={item.id}
                spacing={1}
                sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
              >
                <Typography fontWeight={600}>{item.title}</Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('osBuddy.suggestion.agentSaid')}: {item.agentText}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {t('osBuddy.suggestion.staffSaid')}: {item.staffText}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {t('osBuddy.suggestion.occurrences', { count: item.occurrenceCount })}
                </Typography>
                <Stack direction="row" spacing={1} flexWrap="wrap">
                  <Button
                    size="small"
                    variant="contained"
                    onClick={() =>
                      void acceptSuggestion.mutate({
                        id: item.id,
                        kind: item.kind,
                        title: item.title,
                        statement: suggestionStatement(item),
                      })
                    }
                    disabled={acceptSuggestion.isPending}
                  >
                    {t('osBuddy.suggestion.accept')}
                  </Button>
                  <Button size="small" onClick={() => openSuggestionEdit(item)}>
                    {t('osBuddy.suggestion.edit')}
                  </Button>
                  <Button size="small" color="error" onClick={() => setRejecting(item)}>
                    {t('osBuddy.suggestion.reject')}
                  </Button>
                </Stack>
              </Stack>
            ))}
          </Stack>
        </Box>
      )}

      {grouped.map((group) => (
        <Box key={group.kind}>
          <Typography variant="subtitle2" sx={{ mb: 1 }}>
            {t(`osBuddy.kind.${group.kind}`)}
          </Typography>
          {group.rules.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              {t('osBuddy.emptyGroup')}
            </Typography>
          ) : (
            <Stack spacing={1}>
              {group.rules.map((rule) => {
                const locked = rule.origin === 'oceanspace';
                return (
                  <Stack
                    key={rule.id}
                    direction={{ xs: 'column', sm: 'row' }}
                    spacing={1}
                    alignItems={{ sm: 'center' }}
                    justifyContent="space-between"
                    sx={{ p: 1.5, border: 1, borderColor: 'divider', borderRadius: 1 }}
                  >
                    <Box>
                      <Typography fontWeight={600}>{rule.title}</Typography>
                      <Typography variant="body2" color="text.secondary">
                        {rule.statement}
                      </Typography>
                      <Chip
                        size="small"
                        sx={{ mt: 0.5 }}
                        label={t(`osBuddy.origin.${rule.origin}`)}
                      />
                    </Box>
                    <Stack direction="row" spacing={1}>
                      <Button size="small" disabled={locked} onClick={() => openEdit(rule)}>
                        {tc('edit')}
                      </Button>
                      <Button
                        size="small"
                        color="error"
                        disabled={locked}
                        onClick={() => setDeleteTarget(rule)}
                      >
                        {tc('delete')}
                      </Button>
                    </Stack>
                  </Stack>
                );
              })}
            </Stack>
          )}
        </Box>
      ))}

      <FormDialog
        open={dialogOpen}
        onClose={() => setDialogOpen(false)}
        title={editing ? t('osBuddy.editTitle') : t('osBuddy.addTitle')}
        isDirty={isDirty}
        onSubmit={() => {
          void save.mutate();
        }}
        submitDisabled={!title.trim() || !statement.trim()}
        isLoading={save.isPending}
      >
        <DialogContent>
        <Stack spacing={2} sx={{ pt: 1 }}>
          <TextField
            select
            label={t('osBuddy.kindLabel')}
            value={kind}
            onChange={(e) => setKind(e.target.value as RuleKind)}
            fullWidth
          >
            {KINDS.map((k) => (
              <MenuItem key={k} value={k}>
                {t(`osBuddy.kind.${k}`)}
              </MenuItem>
            ))}
          </TextField>
          <TextField
            label={t('osBuddy.titleLabel')}
            value={title}
            onChange={(e) => setTitle(e.target.value)}
            fullWidth
          />
          <TextField
            label={t('osBuddy.statementLabel')}
            value={statement}
            onChange={(e) => setStatement(e.target.value)}
            fullWidth
            multiline
            minRows={3}
          />
        </Stack>
        </DialogContent>
      </FormDialog>

      <FormDialog
        open={accepting !== null}
        onClose={() => setAccepting(null)}
        title={t('osBuddy.suggestion.editTitle')}
        isDirty={Boolean(accepting)}
        onSubmit={() => {
          if (!accepting) return;
          void acceptSuggestion.mutate({
            id: accepting.id,
            kind,
            title: title.trim(),
            statement: statement.trim(),
          });
        }}
        submitDisabled={!title.trim() || !statement.trim()}
        isLoading={acceptSuggestion.isPending}
      >
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              select
              label={t('osBuddy.kindLabel')}
              value={kind}
              onChange={(e) => setKind(e.target.value as RuleKind)}
              fullWidth
            >
              {KINDS.map((k) => (
                <MenuItem key={k} value={k}>
                  {t(`osBuddy.kind.${k}`)}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label={t('osBuddy.titleLabel')}
              value={title}
              onChange={(e) => setTitle(e.target.value)}
              fullWidth
            />
            <TextField
              label={t('osBuddy.statementLabel')}
              value={statement}
              onChange={(e) => setStatement(e.target.value)}
              fullWidth
              multiline
              minRows={3}
            />
          </Stack>
        </DialogContent>
      </FormDialog>

      <ConfirmDialog
        open={deleteTarget !== null}
        title={t('osBuddy.deleteTitle')}
        message={t('osBuddy.deleteConfirm')}
        confirmColor="error"
        onConfirm={() => {
          if (deleteTarget) void remove.mutate(deleteTarget.id);
        }}
        onCancel={() => setDeleteTarget(null)}
      />

      <ConfirmDialog
        open={rejecting !== null}
        title={t('osBuddy.suggestion.rejectTitle')}
        message={t('osBuddy.suggestion.rejectConfirm')}
        confirmColor="error"
        onConfirm={() => {
          if (rejecting) void rejectSuggestion.mutate(rejecting.id);
        }}
        onCancel={() => setRejecting(null)}
      />
    </Stack>
  );
}
